import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Divider, Typography } from '@mui/material'

import { selectMiscItems } from '@/app/services/miscItems'
import { selectQuoteItems, useUpdateQuoteItemMutation } from '@/app/services/quoteItems'
import { selectQuote } from '@/app/services/quotes'
import { selectLocale } from '@/app/slices/appSlice'
import {
    selectOrganisation,
    selectOrganisationId,
    selectPaidFeatures,
    selectUseImperialUnits,
} from '@/app/slices/organisationSlice'
import { selectedQuoteStatus, selectIsCalculatingQuote } from '@/app/slices/quoteItemsSlice'
import { formatNumber } from '@/common/helpers/formatUtilities'
import { useToolBoxTreatments } from '@/common/hooks'
import { getLargestDimensions, QUOTE_STATUS_ARRAY, secondsToHMS } from '@/common/utils'
import { isQuoteItemCalculated, quoteHasIssues, quoteIssuesCount } from '@/common/utils/quoteUtilities'

import QuoteItemStatus from '../common/QuoteItemStatus'

const classes = {
    summaryContainer: {
        borderRadius: 2,
        border: '1px solid #B0BEC5',
        background: '#FFF',
        minHeight: '624px',
    },
}

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const Summary = () => {
    const { t } = useTranslation()
    const { quoteId } = useParams()

    const organisation = useSelector(selectOrganisation)
    const organisationId = useSelector(selectOrganisationId)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const locale = useSelector(selectLocale)
    const paidFeatures = useSelector(selectPaidFeatures)
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)

    const { showMiscItems } = useToolBoxTreatments()

    const quote = useSelector((state) => selectQuote(state, { organisationId, quoteId }))
    const quoteItems = useSelector((state) => selectQuoteItems(state, { organisationId, quoteId }))
    const miscItems = useSelector((state) => selectMiscItems(state, { organisationId, quoteId }))

    const hideTimes = useSelector(selectedQuoteStatus) === QUOTE_STATUS_ARRAY.NotCalculated

    const isMiscItemsEnabled = showMiscItems && paidFeatures?.hasMiscellaneousItems

    const [, { isLoading: isUpdatingQuoteItem }] = useUpdateQuoteItemMutation({
        fixedCacheKey: 'updateQuoteItem',
    })

    const isPerformingAsyncActions = isUpdatingQuoteItem || isCalculatingQuote

    const areAllItemsCalculated = useMemo(() => {
        return quoteItems?.every(isQuoteItemCalculated)
    }, [quoteItems])

    const hasIssues = useMemo(() => quoteHasIssues(quoteItems), [quoteItems])
    const issuesCount = useMemo(() => quoteIssuesCount(quoteItems), [quoteItems])

    const totalCuttingTime = quoteItems.reduce((total, quoteItem) => {
        const itemCuttingTime =
            (quoteItem?.setupTimePerItemSeconds ?? 0) +
            (quoteItem?.sheetChangeTimePerItemSeconds ?? 0) +
            (quoteItem?.runtime ?? 0) +
            (quoteItem?.unloadTimePerItemSeconds ?? 0)
        return total + itemCuttingTime * quoteItem.quantity
    }, 0)
    const totalFoldingTime = quoteItems.reduce(
        (total, quoteItem) => total + (quoteItem.foldingTotalTimePerItemSeconds ?? 0) * quoteItem.quantity,
        0
    )
    const totalSecProcessesTime = quoteItems.reduce(
        (total, quoteItem) => total + (quoteItem.secondaryProcessTotalTimePerItemSeconds ?? 0) * quoteItem.quantity,
        0
    )

    const totalmiscItemsTime = miscItems.reduce(
        (total, miscItem) => total + (miscItem.productionTimePerItemSeconds ?? 0) * miscItem.quantity,
        0
    )

    const totalMiscItemsWeight = miscItems.reduce(
        (total, miscItem) => total + (miscItem.weight ?? 0) * miscItem.quantity,
        0
    )

    const totalTime = totalCuttingTime + totalFoldingTime + totalSecProcessesTime + totalmiscItemsTime

    const largestDimensions = getLargestDimensions(quoteItems, organisation)

    return (
        <Box
            data-testid="quote-summary-panel"
            sx={[
                {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 3,
                },
                classes.summaryContainer,
            ]}
        >
            <ItemRow>
                <Typography
                    data-testid="quote-summary-section-title"
                    variant="strong1"
                >
                    {t('SUMMARY')}
                </Typography>
                {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions ? (
                    <Box
                        style={{ gap: '6px' }}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                        }}
                    >
                        {hasIssues && Boolean(issuesCount.errors) ? (
                            <QuoteItemStatus
                                count={issuesCount.errors}
                                status="error"
                            />
                        ) : null}
                        {hasIssues && Boolean(issuesCount.warnings) ? (
                            <QuoteItemStatus
                                count={issuesCount.warnings}
                                status="warning"
                            />
                        ) : null}
                        {issuesCount.success ? (
                            <QuoteItemStatus
                                count={issuesCount.success}
                                status="success"
                            />
                        ) : null}
                    </Box>
                ) : null}
            </ItemRow>
            <ItemRow>
                <Typography
                    sx={{
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                    }}
                    variant="small"
                >
                    {t('Parts')}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Number of unique parts')}</Typography>
                <Typography variant="strong2">{quoteItems.length}</Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Net weight')}</Typography>
                <Typography variant="strong2">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? `${formatNumber(quote.totalMass, locale)} ${useImperialUnits ? 'lb' : 'kg'}`
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Largest long-dimension of a part')}</Typography>
                <Typography variant="strong2">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? largestDimensions.long
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Largest short-dimension of a part')}</Typography>
                <Typography variant="strong2">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? largestDimensions.short
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <Divider flexItem />
            {isMiscItemsEnabled ? (
                <>
                    <ItemRow>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                            }}
                            variant="small"
                        >
                            {t('Miscellaneous Items')}
                        </Typography>
                    </ItemRow>
                    <ItemRow>
                        <Typography variant="body2">{t('Number of unique items')}</Typography>
                        <Typography variant="strong2">{miscItems.length}</Typography>
                    </ItemRow>
                    <ItemRow>
                        <Typography variant="body2">{t('Total weight')}</Typography>
                        <Typography variant="strong2">
                            {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                                ? `${formatNumber(totalMiscItemsWeight, locale)} ${useImperialUnits ? 'lb' : 'kg'}`
                                : String.fromCharCode(8212)}
                        </Typography>
                    </ItemRow>
                    <Divider flexItem />
                </>
            ) : null}
            <ItemRow>
                <Typography
                    sx={{
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                    }}
                    variant="small"
                >
                    {t('Times')}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Total cutting time')}</Typography>
                <Typography variant="strong2">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? secondsToHMS(totalCuttingTime)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Total folding time')}</Typography>
                <Typography variant="strong2">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? secondsToHMS(totalFoldingTime)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography variant="body2">{t('Total secondary processes time')}</Typography>
                <Typography variant="strong2">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? secondsToHMS(totalSecProcessesTime)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            {isMiscItemsEnabled ? (
                <ItemRow>
                    <Typography variant="body2">{t('Total miscellaneous items time')}</Typography>
                    <Typography variant="strong2">
                        {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                            ? secondsToHMS(totalmiscItemsTime)
                            : String.fromCharCode(8212)}
                    </Typography>
                </ItemRow>
            ) : null}
            <ItemRow>
                <Typography variant="strong1">{t('Total time')}</Typography>
                <Typography variant="strong1">
                    {!hideTimes && areAllItemsCalculated && !isPerformingAsyncActions
                        ? secondsToHMS(totalTime)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
        </Box>
    )
}

export default Summary
